const cortexSiteId = (value) => (value == null ? '' : String(value).trim());
const assignedSiteIdFromEnvironmentSite = (site) => cortexSiteId(site.metadata?.orgSiteId) || cortexSiteId(site.orgSiteId);
/**
 * Deduplicate by environment id. Users may have access to multiple sites per environment;
 * keep the first site for study-card display and collect every assigned Cortex site id.
 */
export const deduplicateEnvironments = (environmentSites) => {
    const environmentsById = new Map();
    return environmentSites.reduce((acc, site) => {
        const { environment, study, path } = site;
        const assignedSiteId = assignedSiteIdFromEnvironmentSite(site);
        const existing = environmentsById.get(environment.id);
        if (!existing) {
            const lastUpdatedAt = environment.updatedAt ?? null;
            const nextEnvironment = {
                ...environment,
                siteMetadata: site.metadata,
                study,
                sitePath: path,
                lastUpdatedAt: lastUpdatedAt ?? null,
                assignedSiteIds: assignedSiteId ? [assignedSiteId] : [],
            };
            environmentsById.set(environment.id, nextEnvironment);
            acc.push(nextEnvironment);
            return acc;
        }
        if (assignedSiteId && !existing.assignedSiteIds?.includes(assignedSiteId)) {
            existing.assignedSiteIds = [...(existing.assignedSiteIds ?? []), assignedSiteId];
        }
        return acc;
    }, []);
};
/**
 * Whether the environment is non-production. Uses the authoritative `deployment.isProd`
 * boolean from MOM (derived from the cluster) rather than matching on `deployment.type`,
 * because `internal`-type environments on production clusters are still production.
 * Requires an explicit `false` so a missing `isProd` fails closed (not treated as test).
 */
export const isTestEnvironment = (env) => env?.deployment?.isProd === false;
/**
 * Firebase is unreachable from mainland China, so CN users need Cortex localization or Sites never
 * gets its base dictionary and stalls on the loading spinner (FIRE-778).
 * Within a study, the study's own region is the best signal: users with both China and non-China
 * studies should keep Firestore strings on non-China studies (SSP-4455).
 * Outside a study there is no such signal, so a China environment anywhere in the user's list is
 * treated as evidence that the user may be in China; stalling them is worse than serving Cortex
 * strings to a mixed-region user.
 */
export const getActiveChinaEnvironment = (environments, studyId) => {
    if (!studyId) {
        return environments.find((environment) => environment.deployment?.region === 'cn');
    }
    const activeEnvironment = environments.find((environment) => +environment.id === +studyId);
    return activeEnvironment?.deployment?.region === 'cn' ? activeEnvironment : undefined;
};
